import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "./useAppContext";
import { Database } from "@/shared/types/database.types";
import { invalidateUserQueries } from "@/shared/utils/cacheInvalidation";
import { logActivity } from "@/shared/utils/activityLogger";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { normalizeNameFields } from "@/shared/utils/names";

type UserUpdate = Database["public"]["Tables"]["users"]["Update"];
type PersonUpdate = Database["public"]["Tables"]["estates"]["Update"];

interface UpdateUserData {
  user?: UserUpdate;
  person?: PersonUpdate;
}

export function useUpdateUser() {
  const { user, person } = useAppContext();
  const queryClient = useQueryClient();

  const updateUser = useMutation({
    mutationFn: async (data: UpdateUserData) => {
      // Only update user table - don't update person table (person is deceased person data)
      if (!data.user || !user?.user_id) {
        throw new Error("No user data or user ID provided");
      }

      const userUpdates = normalizeNameFields(data.user);

      const { data: updatedUser, error } = await supabase
        .from("users")
        .update(userUpdates)
        .eq("user_id", user.user_id)
        .select()
        .single();

      if (error) throw error;

      // Sync contact-relevant fields to the "Me" person_contacts row
      const contactUpdates: Record<string, string | null> = {};
      if (userUpdates.first_name !== undefined)
        contactUpdates.first_name = userUpdates.first_name ?? null;
      if (userUpdates.last_name !== undefined)
        contactUpdates.last_name = userUpdates.last_name ?? null;
      if (data.user.phone_number !== undefined)
        contactUpdates.phone = data.user.phone_number ?? null;

      if (user.email && Object.keys(contactUpdates).length > 0 && user.last_active_estate_id) {
        await supabase
          .from("estate_contacts")
          .update(contactUpdates)
          .eq("estate_id", user.last_active_estate_id)
          .eq("email", user.email);
      }

      if (data.user.address !== undefined && user.user_id) {
        void resolveAddressLink({
          raw: data.user.address ?? "",
          purpose: "mailing",
          entity: { user_id: user.user_id },
        }).finally(() => {
          void queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] });
        });
      }

      const estateId = person?.estate_id ?? user.last_active_estate_id;
      if (estateId) {
        logActivity({
          estate_id: estateId,
          action_type: "updated_user_profile",
          user_id: user.user_id,
          entity_type: "user",
          entity_id: String(user.user_id),
          metadata: { ...data.user },
        }).catch(() => {});
      }

      return updatedUser;
    },
    onSuccess: () => {
      // Comprehensive cache invalidation for all user-related queries
      invalidateUserQueries(queryClient, {
        userId: user?.auth_id || user?.user_id?.toString(),
      });
      queryClient.invalidateQueries({ queryKey: ["person-contacts"] });
    },
  });

  return {
    updateUser: updateUser.mutate,
    updateUserAsync: updateUser.mutateAsync,
    isUpdating: updateUser.isPending,
    error: updateUser.error,
  };
}

// Specialized hook for updating notification preferences
export function useUpdateNotifications() {
  const { user } = useAppContext();
  const queryClient = useQueryClient();

  const updateNotifications = useMutation({
    mutationFn: async (preferences: { contact_by_email?: boolean; contact_by_phone?: boolean }) => {
      if (!user?.user_id) throw new Error("No user found");

      const { data, error } = await supabase
        .from("users")
        .update(preferences)
        .eq("user_id", user.user_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Comprehensive cache invalidation for all user-related queries
      invalidateUserQueries(queryClient, {
        userId: user?.auth_id || user?.user_id?.toString(),
      });
    },
  });

  return {
    updateNotifications: updateNotifications.mutate,
    isUpdating: updateNotifications.isPending,
    error: updateNotifications.error,
  };
}
